import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { SCAN_EVENT_LABELS, lookupBox, type BoxEventType, type LookupBox } from '@ecobox/domain';
import {
  scanCodeKind,
  shipmentPaidInFull,
  watchOpsBox,
  watchOpsBoxScans,
  watchOpsShipment,
  type BoxRow,
  type LookupState,
  type OpsBoxScan,
  type ShipmentRow,
} from '@ecobox/firebase';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ScanInput,
  ScannerView,
  TimeInState,
  playBeep,
  type BadgeTone,
} from '@ecobox/ui';
import { useWatch } from '../hooks/useWatch';
import { loadBarcodeDetector } from '../scanner/detector';
import { useBranch, useServices, useSession, useSyncState } from '../services';
import { usePermissions } from '../sessions';

const NO_SCANS: OpsBoxScan[] = [];

/** The tab that does an action, by its label on the Scan page. */
const TAB_PATHS: Readonly<Record<string, string>> = {
  Load: '/load',
  Receive: '/receive',
  'Hand over': '/handover',
};

const PAYMENT_LABELS: Readonly<Record<string, string>> = {
  unpaid: 'Not paid',
  partial: 'Part paid',
  paid: 'Paid',
  overpaid: 'Paid',
};

/** Follows one document lookup; `null` waits. */
function useLookup<T>(
  start: ((onData: (s: LookupState<T>) => void) => () => void) | null,
): LookupState<T> {
  // The answer remembers which lookup it belongs to, so a new lookup reads as loading at once.
  const [state, setState] = useState<{ of: unknown; value: LookupState<T> } | null>(null);
  useEffect(() => {
    if (!start) return;
    return start((value) => setState({ of: start, value }));
  }, [start]);
  return start && state?.of === start ? state.value : LOADING;
}

const LOADING = { kind: 'loading' } as const;

/**
 * Scan (05 §5.2): scan or type any box label and see what it is, where it is, its shipment, its
 * last scans here and what can be done with it now. It changes nothing: it only reads, and works
 * from the phone's copy when there is no signal.
 */
export function ScanPage() {
  const [code, setCode] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [status, setStatus] = useState<'idle' | 'ok' | 'error'>('idle');
  const [camera, setCamera] = useState(false);

  const onScan = (raw: string): void => {
    const value = raw.trim().toUpperCase();
    const kind = scanCodeKind(value);
    if (kind === 'unit') {
      setCode(value);
      setNotice(null);
      setStatus('ok');
      return;
    }
    setStatus('error');
    playBeep('error');
    setNotice(
      kind === 'tracking'
        ? `${value} is a tracking number. Scan the label on one of its boxes.`
        : `${value} is not an EcoBox box label. A box label looks like EB0000012344.`,
    );
  };

  return (
    <div className="ops-stack">
      <h1 className="ops-title">Scan</h1>
      <p className="ops-muted" style={{ margin: 0 }}>
        Scan any box to see what it is, where it is and what you can do with it.
      </p>
      <div data-tour="scan-field">
        <ScanInput onScan={onScan} beep status={status} label="Scan or type a box label" />
      </div>
      <Button size="touch" variant="secondary" block onClick={() => setCamera(true)}>
        Scan with the camera
      </Button>
      {notice ? (
        <p role="alert" className="ops-error">
          {notice}
        </p>
      ) : null}
      {code ? (
        <BoxLookup key={code} unitId={code} />
      ) : (
        <EmptyState
          title="Nothing scanned yet"
          description="Scan a box label, or type it and press Enter. It works with no signal for boxes this phone has seen."
        />
      )}
      {camera ? (
        <ScannerView
          loadDetector={loadBarcodeDetector}
          title="Look up a box"
          count={code ? 1 : 0}
          countLabel="looked up"
          lastResult={null}
          onRead={(read) => {
            setCamera(false);
            onScan(read);
            return 'ok';
          }}
          onClose={() => setCamera(false)}
        />
      ) : null}
    </div>
  );
}

function BoxLookup({ unitId }: { unitId: string }) {
  const { client } = useServices();
  const branch = useBranch().current;
  const sync = useSyncState();
  const { claims } = useSession();
  const perms = usePermissions();

  const startBox = useCallback(
    (onData: (s: LookupState<BoxRow>) => void) => watchOpsBox(client.db, unitId, onData),
    [client.db, unitId],
  );
  const box = useLookup(startBox);
  const trackingNo = box.kind === 'found' ? box.row.trackingNo : null;
  const startShipment = useCallback(
    (onData: (s: LookupState<ShipmentRow>) => void) =>
      watchOpsShipment(client.db, trackingNo ?? '', onData),
    [client.db, trackingNo],
  );
  const shipment = useLookup(trackingNo ? startShipment : null);
  // A driver reads only what is planned for them (07 §7.3): the branch's scan log is for the office.
  const roles = claims?.roleIds ?? [];
  const plainDriver = roles.length === 1 && roles[0] === 'driver';
  const startScans = useCallback(
    (d: (rows: OpsBoxScan[]) => void, e: (err: Error) => void) =>
      watchOpsBoxScans(client.db, { unitId, branchId: branch ?? '' }, d, e),
    [client.db, unitId, branch],
  );
  const scans = useWatch(
    box.kind === 'found' && branch && !plainDriver ? startScans : null,
    NO_SCANS,
  );

  if (box.kind === 'loading') {
    return (
      <p className="ops-muted" role="status">
        Looking up {unitId}…
      </p>
    );
  }
  if (box.kind !== 'found') {
    const words = {
      missing: {
        title: `No box ${unitId}`,
        text: 'This label is not on any shipment. Check the label. If the box is real, tell your supervisor.',
      },
      not_on_phone: {
        title: `${unitId} is not on this phone`,
        text: 'There is no signal, and this phone has not seen this box before. Try again when the signal returns.',
      },
      not_allowed: {
        title: 'You cannot see this box',
        text: 'It is at a branch outside your access, or it is not on one of your runs. Ask your supervisor.',
      },
      error: {
        title: 'The box could not be looked up',
        text: box.kind === 'error' ? box.message : '',
      },
    }[box.kind];
    return (
      <div role="status" data-tour="scan-box">
        <EmptyState title={words.title} description={words.text} />
      </div>
    );
  }

  const row = box.row;
  const paid = shipment.kind === 'found' ? shipmentPaidInFull(shipment.row) : undefined;
  const view = lookupBox({
    box: row as unknown as LookupBox,
    branchId: branch,
    shipmentPaidInFull: paid,
    holds: (p) => perms.has(p),
  });
  const last = row.lastEvent;
  const tone: BadgeTone = view.warnings.length > 0 ? 'warning' : view.here ? 'success' : 'neutral';
  const canReport = perms.has('exception.raise');

  return (
    <div className="ops-stack">
      <Card title="The box" data-tour="scan-box">
        <div className="ops-stack" style={{ gap: 8 }}>
          <div className="ops-row__top">
            <span className="ops-code">{row.barcode}</span>
            <Badge tone={tone}>{view.statusLabel}</Badge>
          </div>
          <div className="ops-detail">{view.where}</div>
          {last ? (
            <div className="ops-muted">
              Last scan: {SCAN_EVENT_LABELS[last.type as BoxEventType] ?? last.type} at{' '}
              {last.branchId}, <TimeInState since={last.at} suffix="ago" />
            </div>
          ) : null}
          {box.fromCache && !sync.online ? (
            <Badge tone="warning">From this phone’s copy: no signal</Badge>
          ) : null}
          {view.warnings.length > 0 ? (
            <ul className="ops-stack" aria-label="Warnings" style={{ margin: 0, paddingLeft: 18 }}>
              {view.warnings.map((w) => (
                <li key={w} className="ops-error">
                  {w}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </Card>

      <Card title="Its shipment" data-tour="scan-shipment">
        {shipment.kind === 'found' ? (
          <div className="ops-stack" style={{ gap: 4 }}>
            <div>
              <span className="ops-code">{shipment.row.trackingNo}</span>{' '}
              <Badge tone={paid ? 'success' : 'danger'}>
                {PAYMENT_LABELS[shipment.row.money.status] ?? shipment.row.money.status}
              </Badge>
            </div>
            <div>
              To {shipment.row.consignee.name}, {shipment.row.consignee.cityName}
            </div>
            <div className="ops-muted">
              {shipment.row.service === 'air'
                ? 'Air cargo'
                : shipment.row.service === 'prime'
                  ? 'Prime courier'
                  : 'Sea cargo'}{' '}
              · {shipment.row.boxCount} {shipment.row.boxCount === 1 ? 'box' : 'boxes'} · from{' '}
              {shipment.row.originBranchId}
            </div>
          </div>
        ) : (
          <div className="ops-stack" style={{ gap: 4 }}>
            <span className="ops-code">{row.trackingNo}</span>
            <span className="ops-muted">
              {shipment.kind === 'loading'
                ? 'Loading the shipment…'
                : shipment.kind === 'not_allowed'
                  ? 'The shipment’s details are not visible to you.'
                  : shipment.kind === 'not_on_phone'
                    ? 'The shipment is not on this phone yet. It shows when the signal returns.'
                    : 'The shipment could not be read.'}
            </span>
          </div>
        )}
      </Card>

      <Card title="What you can do" data-tour="scan-actions">
        {view.actions.length === 0 && !canReport ? (
          <p className="ops-muted" style={{ margin: 0 }}>
            Nothing for you to do with this box here.
          </p>
        ) : (
          <ul className="ops-list" aria-label="Actions">
            {view.actions.map((a) => (
              <li key={a.event} className="ops-row" data-tone={a.refused ? 'bad' : 'ok'}>
                <div className="ops-row__top">
                  <strong>{a.label}</strong>
                  {a.refused ? (
                    <Badge tone="danger">Refused: {a.refused}</Badge>
                  ) : TAB_PATHS[a.where] ? (
                    <Link to={TAB_PATHS[a.where] ?? '/today'}>On {a.where}</Link>
                  ) : (
                    <span className="ops-muted">On {a.where}</span>
                  )}
                </div>
              </li>
            ))}
            {canReport ? (
              <li className="ops-row">
                <div className="ops-row__top">
                  <strong>Damaged or something wrong?</strong>
                  <Link to={`/exception?kind=box&id=${encodeURIComponent(row.barcode)}`}>
                    Report a problem
                  </Link>
                </div>
              </li>
            ) : null}
          </ul>
        )}
      </Card>

      {plainDriver ? null : (
        <Card title={`Scans at ${branch ?? 'this branch'}`} data-tour="scan-history">
          {scans.error ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              The scans here are not visible to you.
            </p>
          ) : scans.data.length === 0 ? (
            <p className="ops-muted" style={{ margin: 0 }}>
              {scans.loading ? 'Loading…' : 'No scans of this box at this branch.'}
            </p>
          ) : (
            <ul className="ops-list" aria-label="Scans here">
              {scans.data.map((s) => (
                <li
                  key={s.id}
                  className="ops-row"
                  data-tone={s.result === 'exception' ? 'bad' : undefined}
                >
                  <div className="ops-row__top">
                    <span>{SCAN_EVENT_LABELS[s.type as BoxEventType] ?? s.type}</span>
                    {s.occurredAt ? <TimeInState since={s.occurredAt} suffix="ago" /> : null}
                  </div>
                  {s.result === 'exception' || s.result === 'duplicate' ? (
                    <div className="ops-muted">
                      {s.result === 'duplicate' ? 'Scanned twice' : 'Flagged for review'}
                      {s.note ? `: ${s.note}` : ''}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}
